import * as React from "react";
import {
  browserLocalStorage,
  BrowserLocalStorageState,
} from "@base/browserLocalStorage";

export function useBrowserLocalStorage() {
  // Lazy-init from storage so `user` (and the rest of state) is available on the
  // very first client render instead of popping in after a mount effect. Returns
  // `{}` during SSR (no `window`), so any markup that depends on this state must
  // be client-gated to avoid hydration mismatches.
  const [state, setState] = React.useState<BrowserLocalStorageState>(() =>
    browserLocalStorage.get(),
  );

  // Mirror every module-level write into React state, wherever it came from
  // (this hook's update/clear, or services like webSession writing directly).
  // The immediate sync() catches writes that landed between the lazy init above
  // and the subscription; `get()` is referentially stable, so no-op syncs bail.
  React.useEffect(() => {
    const sync = () => setState(browserLocalStorage.get());
    sync();
    return browserLocalStorage.subscribe(sync);
  }, []);

  // Write-through: persist synchronously inside update/clear rather than in a
  // post-render effect, so a write followed by a hard navigation in the same
  // tick (e.g. storing the handoff code and redirecting) cannot be lost
  // to the page unloading before React flushes. Merging from storage (not from
  // React state) keeps same-tick consecutive updates compounding correctly.
  const update = React.useCallback(
    (data: Partial<BrowserLocalStorageState>) => {
      browserLocalStorage.set({ ...browserLocalStorage.get(), ...data });
    },
    [],
  );

  const clear = React.useCallback(() => {
    browserLocalStorage.clearAll();
  }, []);

  return { state, update, clear };
}
